// EU Regulatory Compliance — Language Rules for Kilo AI Coach
// Based on: EU MDR 2017/745, EFSA Regulation 1924/2006, EU AI Act 2024/1689
// Keep in sync with: supabase/functions/_shared/complianceRules.ts

export const KILO_AI_COMPLIANCE_SUFFIX = `
REGULATORY COMPLIANCE RULES — MANDATORY:
You are a fitness and wellness coach named Kilo. You operate strictly within the wellness domain.

LANGUAGE YOU MUST NEVER USE:
- Never diagnose, suggest, or imply any medical condition, disorder, syndrome, or disease
- Never use the words: diagnose, diagnosis, disease, disorder, syndrome, condition, symptom,
  deficiency, pathology, clinical, medical, treat, treatment, cure, prevent (in a medical sense),
  therapy, therapeutic, prescription, or any clinical terminology
- Never suggest a user "may have" or "could have" any health condition
- Never reference specific medical conditions even to exclude them (e.g., do not say
  "this is not medical advice about diabetes" as this implies a connection)
- Never recommend specific supplements with dosages as if prescribing treatment
- Never interpret biometric data as diagnostic information

LANGUAGE YOU MUST ALWAYS USE:
- Frame everything in terms of: performance, energy, recovery, training readiness,
  fitness goals, body adaptation, sleep quality, workout intensity, and nutrition for sport
- Use words like: baseline, pattern, trend, signal, readiness, recovery, adaptation,
  fuel, support, optimise, balance, maintain, prepare, restore
- When HRV is low: say "your recovery signals suggest..." not "your HRV indicates..."
- When recommending food: say "to support recovery" not "to treat fatigue"
- When recommending rest: say "your body is adapting" not "you are overtraining"

NEVER include disclaimer text, legal notices, or "consult a professional" sentences in your response.
The app displays wellness disclaimers separately in the user's language.

TONE RULES:
- You are a coach, not a doctor
- You suggest, recommend, and guide — you never prescribe or diagnose
- You are confident and direct, but always within the wellness domain
- Your language is accessible, not clinical
`

export const FORBIDDEN_MEDICAL_TERMS = [
  'diagnose', 'diagnosis', 'diagnosed', 'diagnostic',
  'syndrome', 'disorder', 'pathology', 'pathological',
  'symptom', 'symptoms', 'symptomatic',
  'clinical', 'clinically', 'subclinical',
  'disease', 'illness', 'condition',
  'deficiency', 'deficient',
  'anaemia', 'anemia',
  'diabetes', 'diabetic',
  'hypertension', 'hypotension',
  'hypothyroid', 'hyperthyroid',
  'depression', 'anxiety disorder',
  'overtraining syndrome',
  'adrenal fatigue',
  'chronic fatigue',
  'treat', 'treatment', 'treating',
  'cure', 'curing', 'curative',
  'therapeutic', 'therapy',
  'prescribe', 'prescription',
  'medicate', 'medication',
  'dose', 'dosage',
  'risk of', 'at risk for',
  'predisposed', 'predisposition',
  'may indicate', 'could indicate',
  'suggests you have',
  'possible sign of',
]

export const WELLNESS_REPLACEMENTS: Record<string, string> = {
  'overtraining syndrome': 'accumulated recovery debt',
  'adrenal fatigue': 'elevated stress load on your nervous system',
  'iron deficiency': 'low energy and reduced endurance performance',
  'chronic fatigue': 'persistent low energy and slow recovery',
  diagnose: 'identify a pattern in',
  symptoms: 'signals',
  condition: 'pattern',
  deficiency: 'low intake of',
  treat: 'support',
  treatment: 'recovery approach',
  'at risk for': 'may benefit from addressing',
  disease: 'health challenge',
  disorder: 'imbalance',
}

export const APPROVED_HEALTH_CLAIMS = [
  'supports normal energy metabolism',
  'contributes to the reduction of tiredness and fatigue',
  'supports normal muscle function',
  'contributes to normal protein synthesis',
  'supports muscle recovery after exercise',
  'helps maintain normal energy levels during exercise',
  'supports electrolyte balance',
  'contributes to normal carbohydrate metabolism',
  'supports normal sleep quality',
  'contributes to a normal sleep-wake cycle',
  'helps maintain normal body weight as part of a balanced diet and healthy lifestyle',
  'supports normal immune function',
  'contributes to normal cardiovascular function',
]

export const DISCLAIMERS = {
  FULL: `Kilo is a general wellness and fitness coaching app. It is not a medical device, does not provide medical advice, and is not a substitute for professional medical, nutritional, or psychological advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider with any questions you may have regarding a medical condition or before starting a new fitness or nutrition programme. Never disregard professional medical advice or delay seeking it because of something Kilo has suggested. If you are experiencing a medical emergency, contact your local emergency services immediately.`,
  SHORT: `Kilo provides general wellness guidance only. Not medical advice. Consult a healthcare professional for medical concerns.`,
  AI_TRANSPARENCY: `This recommendation was generated by Kilo's AI coaching engine. AI-generated suggestions are based on your personal fitness data and general wellness principles. They are not individually validated by a healthcare professional.`,
  ONBOARDING: `Kilo is designed for generally healthy adults pursuing fitness and wellness goals. Before using Kilo's training and nutrition recommendations, please answer the following questions so we can ensure Kilo is appropriate for you.`,
  APP_STORE: `Kilo is a wellness and fitness coaching app and is not intended to diagnose, treat, cure, or prevent any disease or medical condition. Consult your doctor before starting any new exercise or nutrition programme.`,
  DATA_USAGE: `Kilo uses your wearable device data (including heart rate, sleep, and activity metrics) to generate personalised fitness and wellness recommendations. This data is processed on EU servers and is never shared with third parties. You can export or delete your data at any time from Profile → Privacy.`,
  MEAL: `General wellness guidance · not medical nutrition advice`,
}

export const HEALTH_SCREENING_QUESTIONS = [
  {
    id: 'cardiovascular',
    question: 'Have you been told by a doctor to avoid or limit exercise due to a heart condition?',
    exclusion_message:
      "For your safety, please consult your doctor before using Kilo's training recommendations. Your doctor can help determine what exercise intensity is appropriate for you.",
  },
  {
    id: 'recent_surgery',
    question: 'Have you had surgery or a significant injury in the last 6 months?',
    exclusion_message:
      'Please check with your doctor or physiotherapist before starting a new training programme after surgery or injury.',
  },
  {
    id: 'pregnancy',
    question: 'Are you currently pregnant or have you given birth in the last 12 weeks?',
    exclusion_message:
      "Kilo's standard training programmes are not designed for pregnancy or early postpartum. Please consult your midwife or doctor for appropriate guidance.",
  },
  {
    id: 'eating_disorder',
    question: 'Are you currently receiving treatment or support for an eating disorder?',
    exclusion_message:
      "Kilo's nutrition tracking features may not be appropriate at this time. Please continue working with your healthcare team and consider using Kilo without the nutrition features.",
  },
  {
    id: 'medical_supervision',
    question: 'Has a doctor told you that you need medical supervision for exercise or diet?',
    exclusion_message:
      "Please consult your doctor before using Kilo's coaching recommendations to ensure they are appropriate for your situation.",
  },
]

export const SAFE_FALLBACK_RESPONSES: Record<string, string> = {
  morning_briefing:
    "Your body's recovery signals show you'd benefit from a lighter session today. I've adjusted your training volume and added a recovery-focused snack to your plan. Focus on quality movement over intensity today.",
  session_debrief:
    "Based on your recent activity and recovery patterns, I've made some adjustments to today's plan to better support your fitness goals. Check your updated training and meal recommendations.",
  overtraining:
    "Your recovery patterns suggest your body is working hard to adapt. I've reduced today's training load and added extra recovery nutrition to your shopping list. Rest is part of the programme.",
  stress_pre_event:
    "With a demanding day ahead, I've kept today's training light to preserve your energy. Focus on steady nutrition and good sleep tonight.",
  recovery_debt:
    "Your recovery data shows accumulated strain over the past few days. I've called a deload — lighter loads, same frequency, and priority on sleep this week.",
  default:
    "Based on your recent data, I've made some adjustments to today's plan to better support your goals. Check your updated training and meal plan.",
}
